import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import type { DeploymentMode, Environment, EnvironmentKind, RollbackPolicy } from '../../api';
import { slugify, useResource } from '../../hooks';
import { ErrorNotice, Field } from '../../components/ui';
import { ConfigurationDrawer } from '../../components/project/ConfigurationDrawer';
import { DeleteProjectPanel } from '../../components/project/DeleteProjectPanel';
import { RuntimeStrategyCard } from '../../components/project/RuntimeStrategyCard';
import { PanelCard, ProjectSection, QuietEmpty, SectionHeader } from '../../components/project/ui';
import { ResponsiveTable } from '../../components/primitives/ResponsiveTable';
import type { Column } from '../../components/primitives/ResponsiveTable';
import {
  deploymentModeDescription,
  deploymentModeLabel,
  environmentKindLabel,
  humanize,
  labelFor,
  rollbackPolicyDescription,
  rollbackPolicyLabel,
} from '../../labels';
import { useProject } from './ProjectContext';
import { Input, Select } from '../../components/primitives';

/**
 * SettingsSection is the project's low-frequency administration (CO-159):
 * what the project is (its repository is configured under Repositories &
 * build), the selected environment's runtime settings, the environments,
 * and deleting the project last. Adding an environment is a drawer rather
 * than a form that occupies the page for the rest of the project's life.
 */
export function SettingsSection() {
  const project = useProject();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);

  const environmentColumns: Column<Environment>[] = [
    {
      id: 'name',
      header: 'Name',
      card: 'title',
      width: 'fill',
      cell: (environment) => (
        <span className="cell-stack">
          <span className="cell-stack__primary">{environment.name}</span>
          <span className="cell-stack__secondary">
            <code>{environment.slug}</code>
          </span>
        </span>
      ),
    },
    { id: 'type', header: 'Type', width: 14, cell: (environment) => labelFor(environmentKindLabel, environment.kind) },
    {
      id: 'mode',
      header: 'How it is deployed',
      width: 24,
      priority: 'secondary',
      cell: (environment) => (project.isWordPress ? 'WordPress site (agent or SSH)' : labelFor(deploymentModeLabel, environment.deployment_mode)),
    },
    {
      id: 'rollback',
      header: 'After a failed deployment',
      width: 24,
      cell: (environment) => labelFor(rollbackPolicyLabel, environment.rollback_policy),
    },
  ];

  return (
    <ProjectSection>
      <PanelCard labelledBy="settings-project" testId="project-settings">
        <SectionHeader
          id="settings-project"
          title="Project"
          actions={
            <Link className="button button--secondary" to={project.sectionPath('repositories')} data-testid="settings-repository-link">
              Repository configuration
            </Link>
          }
        />
        <dl className="settings-facts">
          <div>
            <dt>Name</dt>
            <dd>{project.project.name}</dd>
          </div>
          <div>
            <dt>Slug</dt>
            <dd>
              <code>{project.project.slug}</code>
            </dd>
          </div>
          <div>
            <dt>{project.isWordPress ? 'Platform' : 'Architecture'}</dt>
            <dd>
              {project.isWordPress ? 'WordPress + WooCommerce' : project.isHeadless ? 'Headless' : 'Magento renders its own storefront'}
            </dd>
          </div>
          <div>
            <dt>Created</dt>
            <dd>{new Date(project.project.created_at).toLocaleDateString()}</dd>
          </div>
        </dl>
      </PanelCard>

      {project.environment ? (
        <RuntimeStrategyCard environmentId={project.environment.id} environmentName={project.environment.name} isWordPress={project.isWordPress} />
      ) : null}

      <PanelCard labelledBy="settings-environments" testId="environments-panel">
        <SectionHeader
          id="settings-environments"
          title="Environments"
          actions={
            <button type="button" className="button button--secondary" onClick={() => setAdding(true)} data-testid="add-environment">
              Add environment
            </button>
          }
        />

        {project.environments.length === 0 ? (
          <QuietEmpty testId="no-environments">
            No environments ·{' '}
            <button type="button" className="link-button" onClick={() => setAdding(true)}>
              Add the first environment
            </button>
          </QuietEmpty>
        ) : (
          <ResponsiveTable
            rows={project.environments}
            rowKey={(environment) => environment.id}
            caption="Environments of this project"
            className="environments-table"
            columns={environmentColumns}
            rowProps={(environment) => ({ highlighted: environment.id === project.environment?.id, testId: `environment-row-${environment.slug}` })}
            actionsSize="lg"
            actions={(environment) => (
              <>
                {environment.id === project.environment?.id ? (
                  <span className="badge badge--neutral">Selected</span>
                ) : (
                  <button type="button" className="button button--secondary button--small" onClick={() => project.selectEnvironment(environment.id)}>
                    Select
                  </button>
                )}
                <Link
                  className="button button--secondary button--small"
                  to={`/app/environments/${environment.id}`}
                  aria-label={`Settings for ${environment.name}`}
                >
                  Settings
                </Link>
              </>
            )}
          />
        )}
      </PanelCard>

      <DeleteProjectPanel
        projectId={project.projectId}
        projectName={project.project.name}
        projectSlug={project.project.slug}
        onDeleted={() => navigate('/app/projects', { state: { deletedProject: project.project.name } })}
      />

      <AddEnvironmentDrawer open={adding} onClose={() => setAdding(false)} />
    </ProjectSection>
  );
}

/**
 * AddEnvironmentDrawer is the former permanent "Add an environment" form,
 * opened only when someone asks for it.
 */
function AddEnvironmentDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const project = useProject();
  const [, setSearchParams] = useSearchParams();
  const options = useResource(() => api.deploymentOptions(), [], undefined, { key: 'deployment-options' });
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [kind, setKind] = useState<EnvironmentKind>('staging');
  // A WordPress environment is always an existing site reached over the
  // agent or SSH; the Magento deployment modes do not apply to it.
  const [mode, setMode] = useState<DeploymentMode>(project.isWordPress ? 'existing_atomic' : 'managed_docker');
  const [policy, setPolicy] = useState<RollbackPolicy>('automatic_code_rollback_when_safe');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const modes = options.data?.deployment_modes ?? (['existing_atomic', 'managed_docker'] as DeploymentMode[]);
  const policies =
    options.data?.rollback_policies ?? (['manual_recovery', 'automatic_code_rollback_when_safe'] as RollbackPolicy[]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const created = await api.createEnvironment(project.projectId, {
        name,
        slug: slug || slugify(name),
        kind,
        deployment_mode: mode,
        rollback_policy: policy,
      });
      setName('');
      setSlug('');
      onClose();
      // The new environment becomes the context straight away; the shell
      // re-reads the project — and with it the environments list — around it.
      setSearchParams(new URLSearchParams({ env: created.slug }), { replace: true });
      project.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ConfigurationDrawer
      open={open}
      title="Add an environment"
      onClose={onClose}
      testId="add-environment-drawer"
    >
      <form className="form" onSubmit={submit}>
        <Field label="Name" hint="For example Staging.">
          <Input required value={name} onChange={(event) => setName(event.target.value)} placeholder="Staging" />
        </Field>
        <Field label="URL slug" hint="Derived from the name when left empty.">
          <Input value={slug} onChange={(event) => setSlug(event.target.value)} placeholder={slugify(name) || 'staging'} />
        </Field>
        <Field
          label="Environment type"
          hint="Never guessed from the name. Production gets the most care."
        >
          <Select value={kind} onChange={(event) => setKind(event.target.value as EnvironmentKind)} data-testid="environment-kind">
            <option value="production">Production — serves real customers</option>
            <option value="staging">Staging — a rehearsal copy (UAT, preprod, QA)</option>
            <option value="development">Development</option>
            <option value="preview">Preview — short-lived</option>
          </Select>
        </Field>
{project.isWordPress ? null : (
        <Field label="How Commerce Ops deploys it" hint={deploymentModeDescription[mode]}>
          <Select value={mode} onChange={(event) => setMode(event.target.value as DeploymentMode)}>
            {modes.map((value) => (
              <option key={value} value={value}>
                {deploymentModeLabel[value] ?? humanize(value)}
              </option>
            ))}
          </Select>
        </Field>
        )}
        <Field label="After a failed deployment" hint={rollbackPolicyDescription[policy]}>
          <Select value={policy} onChange={(event) => setPolicy(event.target.value as RollbackPolicy)}>
            {policies.map((value) => (
              <option key={value} value={value}>
                {rollbackPolicyLabel[value] ?? humanize(value)}
              </option>
            ))}
          </Select>
        </Field>
        <ErrorNotice error={error} />
        <div className="pill-list">
          <button className="button button--primary" type="submit" disabled={submitting || name.trim() === ''}>
            {submitting ? 'Adding' : 'Add environment'}
          </button>
          <button type="button" className="button button--secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
        </div>
      </form>
    </ConfigurationDrawer>
  );
}
